import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import {
  applyTheme,
  readStoredTheme,
  ThemeContext,
  writeStoredTheme,
  type ResolvedTheme,
  type Theme,
  type ThemeContextValue,
} from "@/lib/theme";

const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";

/** Subscribes to the OS colour scheme. No-op where matchMedia is unavailable. */
function subscribeToColorScheme(onChange: () => void): () => void {
  if (typeof matchMedia !== "function") {
    return () => undefined;
  }
  const query = matchMedia(COLOR_SCHEME_QUERY);
  query.addEventListener("change", onChange);
  return () => {
    query.removeEventListener("change", onChange);
  };
}

function osPrefersDark(): boolean {
  return typeof matchMedia === "function" && matchMedia(COLOR_SCHEME_QUERY).matches;
}

/** Exports a component and nothing else, so Fast Refresh stays reliable. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  /*
   * The OS colour scheme is external mutable state, so it is read through
   * useSyncExternalStore rather than mirrored into component state from an
   * effect.
   *
   * An earlier attempt called applyTheme("system") from a media-query
   * listener: that repainted the DOM but triggered no re-render, so the page
   * went dark while the context still reported resolved: "light". Mirroring it
   * with setState inside the effect fixed the staleness but introduced a
   * double render, which the React Compiler lint (react-hooks/set-state-in-effect)
   * correctly rejected. Subscribing is the answer to both: `resolved` is
   * derived during render and is never stale.
   */
  const prefersDark = useSyncExternalStore(
    subscribeToColorScheme,
    osPrefersDark,
    () => false, // server/prerender: assume light rather than flashing
  );

  const resolved: ResolvedTheme =
    theme === "system" ? (prefersDark ? "dark" : "light") : theme;

  // The effect now does one thing: paint what was derived above.
  useEffect(() => {
    applyTheme(theme);
  }, [theme, resolved]);

  const setTheme = useCallback((next: Theme) => {
    writeStoredTheme(next);
    setThemeState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolved, setTheme }),
    [theme, resolved, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
